import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import QRCode from "qrcode";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToUserWallet,
  cleanupWalletListeners,
} from "../../redux/transactionsSlice";
import logo from "../../assets/cosmoola/logo.jpg";
import {
  ArrowLeft, Search, User, Smartphone, Building2, Globe,
  Copy, Check, CheckCircle, AlertCircle, Loader2, Eye, EyeOff,
  X, QrCode, Shield, Clock, ChevronRight, ArrowDownLeft,
  Send, RefreshCw, Link, Phone, Wallet,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   THEME  (same as AddMoney / Transfer)
───────────────────────────────────────────────────────── */
const D = {
  bg:"#000000", surface:"#0a0a0a", surface2:"#111111",
  border:"#1c1c1c", border2:"#2a2a2a", white:"#ffffff",
  offwhite:"#e8e8e8", muted:"#6b6b6b", subtle:"#3a3a3a",
  gold:"#C9A96E",
};
const L = {
  bg:"#ffffff", surface:"#fafafa", surface2:"#f3f3f3",
  border:"#e5e5e5", border2:"#d4d4d4", text:"#0a0a0a",
  textSoft:"#525252", muted:"#a3a3a3",
};

const API = "https://europe-west3-cosmoola.cloudfunctions.net";
const PAYMENT_BASE = "https://cosmoola.com/pay";

const fmt = (n: number) =>
  "€" + Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 });

/* ─────────────────────────────────────────────────────────
   BANK / MOBILE DETAILS  (shared with PaymentMethods)
───────────────────────────────────────────────────────── */
const BANK = {
  local: {
    accountHolder: "Cosmoola Technologies OÜ",
    iban:          "EE38 2200 2210 2014 5685",
    bic:           "HABAEE2X",
    bankName:      "Cosmoola Banking Partner",
  },
  international: {
    accountHolder:    "Cosmoola Technologies OÜ",
    iban:             "EE38 2200 2210 2014 5685",
    bic:              "HABAEE2X",
    correspondentBank:"Deutsche Bank AG",
    correspondentBIC: "DEUTDEDB",
  },
};

const MOBILE_PAYBILLS: Record<string, { label: string; paybill: string; flag: string; currency: string }> = {
  mpesa_ke:   { label:"M-Pesa Kenya",    paybill:"174379", flag:"🇰🇪", currency:"KES" },
  mtn_gh:     { label:"MTN MoMo Ghana",  paybill:"MoMo",   flag:"🇬🇭", currency:"GHS" },
  momo_ng:    { label:"MoMo Nigeria",    paybill:"MoMoNG", flag:"🇳🇬", currency:"NGN" },
  bkash_bd:   { label:"bKash",           paybill:"01779-054290", flag:"🇧🇩", currency:"BDT" },
  jazzcash_pk:{ label:"JazzCash",        paybill:"786",    flag:"🇵🇰", currency:"PKR" },
  airtel_ke:  { label:"Airtel Money",    paybill:"220220", flag:"🇰🇪", currency:"KES" },
};

/* ─────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────── */
type ReceiveTab = "cosmoola" | "mobile" | "sepa" | "swift";

type CosmoolaUser = {
  userId:         string;
  username:       string | null;
  displayName:    string;
  profilePicture: string | null;
};

type PaymentRequest = {
  id:               string;
  requesterId:      string;
  requesterName:    string;
  requesterUsername:string;
  payerId:          string | null;
  payerName:        string;
  amountEUR:        number;
  note:             string;
  status:           "pending" | "paid" | "cancelled" | "expired";
  expiresAt:        string | null;
  createdAt:        string | null;
  paidAt:           string | null;
};

/* ─────────────────────────────────────────────────────────
   COPY FIELD
───────────────────────────────────────────────────────── */
const CopyField: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ marginBottom:12 }}>
      <p style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", marginBottom:5 }}>{label}</p>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:L.surface2, border:`1px solid ${L.border}`, padding:"11px 14px" }}>
        <span style={{ fontSize:13, color:L.text, fontFamily: mono?"'Courier New',monospace":"inherit", fontWeight:500 }}>{value}</span>
        <button onClick={handle} style={{ background:"none", border:"none", cursor:"pointer", padding:"0 0 0 12px", color:copied?"#2d7a2d":L.muted, display:"flex", alignItems:"center", gap:4, fontSize:10, letterSpacing:"0.05em", fontFamily:"'Montserrat',sans-serif", transition:"color 0.2s", flexShrink:0 }}>
          {copied ? <><Check size={12}/> COPIED</> : <><Copy size={12}/> COPY</>}
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   USER AVATAR
───────────────────────────────────────────────────────── */
const Avatar: React.FC<{ user: CosmoolaUser; size?: number }> = ({ user, size = 44 }) => {
  const initials = user.displayName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
  return user.profilePicture ? (
    <img src={user.profilePicture} alt={user.displayName}
      style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover", flexShrink:0 }}/>
  ) : (
    <div style={{ width:size, height:size, borderRadius:"50%", background:D.surface2, border:`1px solid ${D.border2}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
      <span style={{ fontSize:size*0.35, fontWeight:600, color:D.gold, fontFamily:"'Montserrat',sans-serif" }}>{initials}</span>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   REQUEST STATUS BADGE
───────────────────────────────────────────────────────── */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    pending:   { bg:"rgba(180,140,0,0.08)",  text:"#b8a000", border:"rgba(180,140,0,0.25)"  },
    paid:      { bg:"rgba(0,180,80,0.08)",   text:"#00a050", border:"rgba(0,180,80,0.25)"   },
    cancelled: { bg:"rgba(150,150,150,0.08)",text:"#888",    border:"rgba(150,150,150,0.2)" },
    expired:   { bg:"rgba(180,30,30,0.08)",  text:"#cc3030", border:"rgba(180,30,30,0.25)"  },
  };
  const c = colors[status] ?? colors.expired;
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", background:c.bg, border:`1px solid ${c.border}` }}>
      <div style={{ width:5, height:5, borderRadius:"50%", background:c.text }}/>
      <span style={{ fontSize:8, fontWeight:700, letterSpacing:"0.18em", color:c.text, fontFamily:"'Montserrat',sans-serif" }}>
        {status.toUpperCase()}
      </span>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   QR CANVAS COMPONENT
───────────────────────────────────────────────────────── */
const QRCanvas: React.FC<{ value: string; size?: number }> = ({ value, size = 200 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (canvasRef.current && value) {
      QRCode.toCanvas(canvasRef.current, value, {
        width: size,
        margin: 2,
        color: { dark: "#000000", light: "#ffffff" },
      }).catch(() => {});
    }
  }, [value, size]);
  return <canvas ref={canvasRef} style={{ display:"block", borderRadius:8 }}/>;
};

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════ */
const ReceiveTransfer: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const { user }   = useSelector((s: RootState) => s.auth);
  const userId     = paramUserId ?? user?.userId ?? "";
  const myUsername = user?.username ?? "";
  const myName     = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const { wallet } = useSelector((s: RootState) => s.transactions);

  /* ── UI state ── */
  const [loaded,          setLoaded]          = useState(false);
  const [balanceHidden,   setBalanceHidden]   = useState(false);
  const [activeTab,       setActiveTab]       = useState<ReceiveTab>("cosmoola");

  /* ── Cosmoola tab state ── */
  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchLoading,   setSearchLoading]   = useState(false);
  const [searchResults,   setSearchResults]   = useState<CosmoolaUser[]>([]);
  const [searchError,     setSearchError]     = useState("");
  const [selectedUser,    setSelectedUser]    = useState<CosmoolaUser | null>(null);
  const [requestAmount,   setRequestAmount]   = useState(10);
  const [requestNote,     setRequestNote]     = useState("");
  const [requesting,      setRequesting]      = useState(false);
  const [requestResult,   setRequestResult]   = useState<{ requestId: string; link: string; note: string; paymentLink: string } | null>(null);
  const [requestError,    setRequestError]    = useState("");
  const [copied,          setCopied]          = useState<string | null>(null);
  const [showQR,          setShowQR]          = useState(false);

  /* ── Open link state ── */
  const [openLinkAmount,  setOpenLinkAmount]  = useState(10);
  const [openLinkNote,    setOpenLinkNote]    = useState("");
  const [openLinkLoading, setOpenLinkLoading] = useState(false);
  const [openLinkResult,  setOpenLinkResult]  = useState<{ requestId: string; paymentLink: string } | null>(null);

  /* ── Request history ── */
  const [requests,        setRequests]        = useState<PaymentRequest[]>([]);
  const [reqLoading,      setReqLoading]      = useState(false);

  /* ── Mobile tab state ── */
  const [activeMobileProvider, setActiveMobileProvider] = useState("mpesa_ke");

  const balance   = wallet?.balance ?? 0;
  const searchRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (userId) dispatch(listenToUserWallet(userId));
    setTimeout(() => setLoaded(true), 80);
    return () => { dispatch(cleanupWalletListeners()); };
  }, [userId, dispatch]);

  useEffect(() => {
    if (userId) loadRequests();
  }, [userId]);

  const loadRequests = async () => {
    setReqLoading(true);
    try {
      const { data } = await axios.post(`${API}/getMyPaymentRequests`, { userId, type: "outgoing" });
      setRequests(data.outgoing ?? []);
    } catch { /* silent */ } finally {
      setReqLoading(false);
    }
  };

  /* ── Debounced user search ── */
  const handleSearch = (q: string) => {
    setSearchQuery(q);
    setSelectedUser(null);
    setSearchError("");
    setSearchResults([]);
    if (searchRef.current) clearTimeout(searchRef.current);
    if (!q.trim() || q.trim().length < 2) return;

    searchRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const cleanQ = q.trim().replace(/^@/, "");
        const { data } = await axios.post(`${API}/lookupUser`, {
          query:    cleanQ,
          searchBy: "username",
        });
        const filtered = (data.results ?? []).filter((u: CosmoolaUser) => u.userId !== userId);
        setSearchResults(filtered);
        if (filtered.length === 0) setSearchError("No Cosmoola user found for @" + cleanQ);
      } catch (err: any) {
        setSearchError(err.response?.data?.error || "Search failed");
      } finally {
        setSearchLoading(false);
      }
    }, 380);
  };

  /* ── Create payment request to specific user ── */
  const handleRequestFromUser = async () => {
    if (!selectedUser || requestAmount < 0.01) return;
    setRequesting(true);
    setRequestError("");
    try {
      const { data } = await axios.post(`${API}/createPaymentRequest`, {
        requesterId: userId,
        payerId:     selectedUser.userId,
        amountEUR:   requestAmount,
        note:        requestNote.trim(),
      });
      setRequestResult({
        requestId:   data.requestId,
        link:        data.paymentLink,
        paymentLink: data.paymentLink,
        note:        requestNote,
      });
      loadRequests();
    } catch (err: any) {
      setRequestError(err.response?.data?.error || "Failed to create request");
    } finally {
      setRequesting(false);
    }
  };

  /* ── Create open payment link ── */
  const handleCreateOpenLink = async () => {
    if (openLinkAmount < 0.01) return;
    setOpenLinkLoading(true);
    try {
      const { data } = await axios.post(`${API}/createPaymentRequest`, {
        requesterId: userId,
        payerId:     null,
        amountEUR:   openLinkAmount,
        note:        openLinkNote.trim(),
      });
      setOpenLinkResult({ requestId: data.requestId, paymentLink: data.paymentLink });
      loadRequests();
    } catch (err: any) {
      setRequestError(err.response?.data?.error || "Failed to create link");
    } finally {
      setOpenLinkLoading(false);
    }
  };

  /* ── Cancel a request ── */
  const handleCancel = async (requestId: string) => {
    try {
      await axios.post(`${API}/cancelPaymentRequest`, { requesterId: userId, requestId });
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: "cancelled" } : r));
    } catch { /* silent */ }
  };

  const copyText = (key: string, val: string) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 2200);
  };

  const resetCosmoola = () => {
    setSelectedUser(null); setSearchQuery(""); setSearchResults([]);
    setRequestResult(null); setRequestError(""); setRequestNote(""); setRequestAmount(10);
  };

  const activeMobileCfg = MOBILE_PAYBILLS[activeMobileProvider];

  /* ─── TABS ─── */
  const TABS: { id: ReceiveTab; label: string; icon: React.ReactNode; sub: string }[] = [
    { id:"cosmoola", label:"Cosmoola",    icon:<Wallet size={14} strokeWidth={1.5}/>,    sub:"Request from users" },
    { id:"mobile",   label:"Mobile Money",icon:<Smartphone size={14} strokeWidth={1.5}/>, sub:"M-Pesa, Airtel, MTN" },
    { id:"sepa",     label:"SEPA",        icon:<Building2 size={14} strokeWidth={1.5}/>,  sub:"EU bank transfer" },
    { id:"swift",    label:"SWIFT",       icon:<Globe size={14} strokeWidth={1.5}/>,      sub:"International wire" },
  ];

  return (
    <div style={{ minHeight:"100vh", background:L.bg, color:L.text, fontFamily:"'Montserrat','Cormorant Garamond',system-ui,sans-serif" }}>
      <style>{RT_CSS}</style>

      {/* ── HEADER ── */}
      <header style={{ position:"sticky", top:0, zIndex:100, background:"rgba(0,0,0,0.96)", backdropFilter:"blur(14px)", borderBottom:`1px solid ${D.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 20px", height:58 }}>
        <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", cursor:"pointer", color:D.offwhite, display:"flex", alignItems:"center", gap:6, fontSize:11, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.05em" }}>
          <ArrowLeft size={16} strokeWidth={1.5}/>
          <span className="rt-back-label">BACK</span>
        </button>
        <div style={{ position:"absolute", left:"50%", transform:"translateX(-50%)" }}>
          <span style={{ color:D.white, fontWeight:300, letterSpacing:"0.35em", fontFamily:"'Cormorant Garamond',serif", fontSize:15 }}>COSMOOLA</span>
        </div>
        <img src={logo} alt="" style={{ width:28, height:28, objectFit:"cover" }}/>
      </header>

      <div className="rt-container">
        <div className="rt-main">

          {/* TITLE */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4 }} style={{ marginBottom:28 }}>
            <h1 style={{ fontSize:28, fontWeight:300, color:L.text, fontFamily:"'Cormorant Garamond',serif", letterSpacing:"0.06em", margin:0 }}>Receive Money</h1>
            <p style={{ fontSize:12, color:L.textSoft, marginTop:6, letterSpacing:"0.05em" }}>Request or share your details to receive funds</p>
          </motion.div>

          {/* BALANCE CARD */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4, delay:0.04 }}
            style={{ background:D.bg, border:`1px solid ${D.border2}`, padding:"22px 22px", marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <p style={{ fontSize:9, color:D.muted, letterSpacing:"0.25em", marginBottom:8 }}>WALLET BALANCE</p>
                <p style={{ fontSize:30, fontWeight:300, color:D.white, fontFamily:"'Cormorant Garamond',serif", letterSpacing:"-0.02em" }}>
                  {balanceHidden ? "€ •••••" : fmt(balance)}
                </p>
              </div>
              <button onClick={() => setBalanceHidden(v => !v)} style={{ background:"none", border:"none", cursor:"pointer", padding:4 }}>
                {balanceHidden ? <Eye size={16} color={D.muted}/> : <EyeOff size={16} color={D.muted}/>}
              </button>
            </div>
            {myUsername && (
              <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${D.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:20, height:20, borderRadius:"50%", background:D.gold, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <span style={{ fontSize:9, fontWeight:700, color:D.bg }}>@</span>
                  </div>
                  <span style={{ fontSize:12, color:D.offwhite, letterSpacing:"0.04em" }}>@{myUsername}</span>
                </div>
                <button
                  onClick={() => copyText("username", `@${myUsername}`)}
                  style={{ display:"flex", alignItems:"center", gap:5, background:"none", border:"none", cursor:"pointer", color:copied==="username"?"#2d7a2d":D.muted, fontSize:10, fontFamily:"'Montserrat',sans-serif", transition:"color 0.2s" }}>
                  {copied==="username"?<><Check size={11}/> COPIED</>:<><Copy size={11}/> COPY USERNAME</>}
                </button>
              </div>
            )}
          </motion.div>

          {/* TABS */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4, delay:0.08 }}
            style={{ display:"flex", borderBottom:`1px solid ${L.border}`, marginBottom:24, overflowX:"auto" }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{
                  display:"flex", alignItems:"center", gap:7, padding:"13px 16px",
                  background:"transparent", border:"none",
                  borderBottom: activeTab===tab.id?`2px solid ${L.text}`:"2px solid transparent",
                  color: activeTab===tab.id?L.text:L.muted,
                  cursor:"pointer", fontSize:10, fontWeight:600, letterSpacing:"0.13em",
                  fontFamily:"'Montserrat',sans-serif", whiteSpace:"nowrap",
                  transition:"all 0.2s", marginBottom:-1,
                }}>
                {tab.icon}
                <span className="rt-tab-label">{tab.label.toUpperCase()}</span>
              </button>
            ))}
          </motion.div>

          <AnimatePresence mode="wait">

            {/* ════════════════════════════════════════
                TAB: COSMOOLA
            ════════════════════════════════════════ */}
            {activeTab === "cosmoola" && (
              <motion.div key="cosmoola" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}>

                {/* ── Request from specific user ── */}
                <div style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"24px 22px", marginBottom:16 }}>
                  <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:16 }}>REQUEST FROM A USER</p>

                  {!requestResult ? (<>
                    {/* Search bar */}
                    <div style={{ position:"relative", marginBottom:14 }}>
                      <div style={{ display:"flex", alignItems:"center", background:L.surface2, border:`1px solid ${searchQuery?L.text:L.border}`, transition:"border-color 0.2s" }}>
                        <div style={{ padding:"0 14px", color:L.muted, display:"flex", alignItems:"center" }}>
                          {searchLoading ? <Loader2 size={16} className="rt-spin"/> : <Search size={16}/>}
                        </div>
                        <input
                          type="text"
                          placeholder="Search by @username"
                          value={searchQuery}
                          onChange={e => handleSearch(e.target.value)}
                          style={{ flex:1, background:"transparent", border:"none", padding:"13px 14px 13px 0", fontSize:14, color:L.text, outline:"none", fontFamily:"'Montserrat',sans-serif" }}
                        />
                        {searchQuery && (
                          <button onClick={() => { setSearchQuery(""); setSearchResults([]); setSearchError(""); setSelectedUser(null); }}
                            style={{ background:"none", border:"none", cursor:"pointer", padding:"0 14px", color:L.muted }}>
                            <X size={14}/>
                          </button>
                        )}
                      </div>

                      {/* Results dropdown */}
                      <AnimatePresence>
                        {(searchResults.length > 0) && !selectedUser && (
                          <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                            style={{ position:"absolute", top:"100%", left:0, right:0, background:L.bg, border:`1px solid ${L.border}`, borderTop:"none", zIndex:40, maxHeight:220, overflowY:"auto" }}>
                            {searchResults.map(u => (
                              <div key={u.userId} onClick={() => { setSelectedUser(u); setSearchQuery(u.displayName); setSearchResults([]); }}
                                style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 16px", cursor:"pointer", borderBottom:`1px solid ${L.border}`, transition:"background 0.14s" }}
                                onMouseEnter={e => (e.currentTarget.style.background = L.surface)}
                                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                              >
                                <Avatar user={u} size={36}/>
                                <div>
                                  <div style={{ fontSize:13, fontWeight:500, color:L.text }}>{u.displayName}</div>
                                  {u.username && <div style={{ fontSize:10, color:L.muted, marginTop:2 }}>@{u.username}</div>}
                                </div>
                                <ChevronRight size={14} color={L.muted} style={{ marginLeft:"auto" }}/>
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Search error */}
                    {searchError && !selectedUser && searchQuery.length >= 2 && (
                      <p style={{ fontSize:11, color:L.muted, marginBottom:12 }}>{searchError}</p>
                    )}

                    {/* Selected user */}
                    <AnimatePresence>
                      {selectedUser && (
                        <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", background:L.surface2, border:`1px solid ${L.border}`, marginBottom:16 }}>
                            <Avatar user={selectedUser} size={40}/>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:13, fontWeight:500, color:L.text }}>{selectedUser.displayName}</div>
                              {selectedUser.username && <div style={{ fontSize:10, color:L.muted, marginTop:2 }}>@{selectedUser.username}</div>}
                            </div>
                            <button onClick={() => { setSelectedUser(null); setSearchQuery(""); }} style={{ background:"none", border:"none", cursor:"pointer", color:L.muted }}>
                              <X size={14}/>
                            </button>
                          </div>

                          {/* Amount */}
                          <div style={{ marginBottom:14 }}>
                            <label style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", display:"block", marginBottom:8 }}>AMOUNT (EUR)</label>
                            <div style={{ display:"flex", gap:0 }}>
                              <div style={{ padding:"0 14px", background:L.surface2, border:`1px solid ${L.border}`, borderRight:"none", display:"flex", alignItems:"center", fontSize:14, color:L.textSoft, fontWeight:500 }}>€</div>
                              <input
                                type="number" min={0.01} step={1}
                                value={requestAmount}
                                onChange={e => setRequestAmount(Math.max(0.01, Number(e.target.value)))}
                                style={{ flex:1, background:L.surface2, border:`1px solid ${L.border}`, padding:"13px 14px", color:L.text, fontSize:15, fontFamily:"'Montserrat',sans-serif", outline:"none", fontWeight:500 }}
                              />
                            </div>
                            {/* Quick amounts */}
                            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
                              {[5,10,25,50,100,250].map(q => (
                                <button key={q} onClick={() => setRequestAmount(q)}
                                  style={{ padding:"6px 12px", border:requestAmount===q?`1px solid ${L.text}`:`1px solid ${L.border}`, background:requestAmount===q?L.text:"transparent", color:requestAmount===q?L.bg:L.textSoft, cursor:"pointer", fontSize:10, fontFamily:"'Montserrat',sans-serif" }}>
                                  €{q}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Note */}
                          <div style={{ marginBottom:16 }}>
                            <label style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", display:"block", marginBottom:8 }}>NOTE (OPTIONAL)</label>
                            <input
                              type="text" maxLength={280}
                              placeholder="What's this for?"
                              value={requestNote}
                              onChange={e => setRequestNote(e.target.value)}
                              style={{ width:"100%", background:L.surface2, border:`1px solid ${L.border}`, padding:"12px 14px", color:L.text, fontSize:13, fontFamily:"'Montserrat',sans-serif", outline:"none" }}
                            />
                          </div>

                          {requestError && (
                            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px", background:"#fff0f0", border:"1px solid #f0c8c8", marginBottom:14 }}>
                              <AlertCircle size={13} color="#B22222"/>
                              <span style={{ fontSize:11, color:"#B22222" }}>{requestError}</span>
                            </div>
                          )}

                          <motion.button whileTap={{ scale:0.97 } as any} onClick={handleRequestFromUser} disabled={requesting||requestAmount<0.01}
                            style={{ width:"100%", padding:"14px 0", background:requesting||requestAmount<0.01?L.border:D.bg, border:"none", color:requesting||requestAmount<0.01?L.muted:D.white, cursor:requesting?"not-allowed":"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.2em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                            {requesting ? <><Loader2 size={14} className="rt-spin"/> SENDING...</> : <><Send size={14}/> REQUEST {fmt(requestAmount)}</>}
                          </motion.button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>) : (
                    /* ── Request sent success ── */
                    <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} style={{ textAlign:"center" }}>
                      <div style={{ width:56, height:56, borderRadius:"50%", background:"#f0f9f0", border:"1px solid #d4ecd4", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
                        <CheckCircle size={26} color="#2d7a2d"/>
                      </div>
                      <h3 style={{ fontSize:18, fontWeight:300, color:L.text, fontFamily:"'Cormorant Garamond',serif", marginBottom:6 }}>Request Sent!</h3>
                      <p style={{ fontSize:12, color:L.textSoft, marginBottom:20 }}>
                        {selectedUser?.displayName} will be notified to pay {fmt(requestAmount)}
                      </p>

                      {/* Payment link */}
                      <div style={{ background:L.surface2, border:`1px solid ${L.border}`, padding:"12px 14px", display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                        <span style={{ fontSize:11, color:L.text, fontFamily:"'Courier New',monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{requestResult.link}</span>
                        <button onClick={() => copyText("link", requestResult.link)}
                          style={{ background:"none", border:"none", cursor:"pointer", color:copied==="link"?"#2d7a2d":L.muted, fontSize:10, display:"flex", alignItems:"center", gap:4, flexShrink:0, marginLeft:8 }}>
                          {copied==="link"?<><Check size={11}/> COPIED</>:<><Copy size={11}/> COPY</>}
                        </button>
                      </div>

                      {/* QR */}
                      <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
                        <div style={{ padding:16, background:"white", border:`1px solid ${L.border}` }}>
                          <QRCanvas value={requestResult.paymentLink} size={160}/>
                        </div>
                      </div>

                      <button onClick={resetCosmoola}
                        style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"11px 24px", background:"transparent", border:`1px solid ${L.border}`, color:L.textSoft, cursor:"pointer", fontSize:11, fontWeight:500, letterSpacing:"0.14em", fontFamily:"'Montserrat',sans-serif" }}>
                        <RefreshCw size={12}/> NEW REQUEST
                      </button>
                    </motion.div>
                  )}
                </div>

                {/* ── Open payment link ── */}
                {!requestResult && (
                  <div style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"24px 22px", marginBottom:16 }}>
                    <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:4 }}>PAYMENT LINK</p>
                    <p style={{ fontSize:11, color:L.muted, marginBottom:16 }}>Create a link anyone can pay — share it, add it to your profile, or show as QR code.</p>

                    {openLinkResult ? (
                      <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} style={{ textAlign:"center" }}>
                        <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
                          <div style={{ padding:14, background:"white", border:`1px solid ${L.border}` }}>
                            <QRCanvas value={openLinkResult.paymentLink} size={180}/>
                          </div>
                        </div>
                        <CopyField label="PAYMENT LINK" value={openLinkResult.paymentLink} mono/>
                        <button onClick={() => setOpenLinkResult(null)}
                          style={{ marginTop:8, display:"inline-flex", alignItems:"center", gap:6, background:"transparent", border:`1px solid ${L.border}`, padding:"9px 18px", cursor:"pointer", fontSize:10, color:L.textSoft, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.12em" }}>
                          <RefreshCw size={11}/> NEW LINK
                        </button>
                      </motion.div>
                    ) : (<>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                        <div>
                          <label style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", display:"block", marginBottom:7 }}>AMOUNT</label>
                          <div style={{ display:"flex" }}>
                            <div style={{ padding:"0 10px", background:L.surface2, border:`1px solid ${L.border}`, borderRight:"none", display:"flex", alignItems:"center", fontSize:13, color:L.textSoft }}>€</div>
                            <input type="number" min={0.01} value={openLinkAmount} onChange={e => setOpenLinkAmount(Math.max(0.01, Number(e.target.value)))}
                              style={{ flex:1, background:L.surface2, border:`1px solid ${L.border}`, padding:"11px 12px", color:L.text, fontSize:13, fontFamily:"'Montserrat',sans-serif", outline:"none" }}/>
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", display:"block", marginBottom:7 }}>NOTE</label>
                          <input type="text" maxLength={100} placeholder="e.g. Lunch split" value={openLinkNote} onChange={e => setOpenLinkNote(e.target.value)}
                            style={{ width:"100%", background:L.surface2, border:`1px solid ${L.border}`, padding:"11px 12px", color:L.text, fontSize:13, fontFamily:"'Montserrat',sans-serif", outline:"none" }}/>
                        </div>
                      </div>
                      <motion.button whileTap={{ scale:0.97 } as any} onClick={handleCreateOpenLink} disabled={openLinkLoading}
                        style={{ width:"100%", padding:"13px 0", background:openLinkLoading?L.border:D.bg, border:"none", color:openLinkLoading?L.muted:D.white, cursor:openLinkLoading?"not-allowed":"pointer", fontSize:11, fontWeight:600, letterSpacing:"0.18em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                        {openLinkLoading?<><Loader2 size={13} className="rt-spin"/> GENERATING...</>:<><Link size={13}/> GENERATE LINK + QR</>}
                      </motion.button>
                    </>)}
                  </div>
                )}

                {/* ── My payment requests ── */}
                {requests.length > 0 && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                      <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em" }}>MY REQUESTS</p>
                      <button onClick={loadRequests} style={{ background:"none", border:"none", cursor:"pointer", color:L.muted, fontSize:10, display:"flex", alignItems:"center", gap:4 }}>
                        <RefreshCw size={11}/> Refresh
                      </button>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {requests.slice(0,8).map(req => (
                        <div key={req.id} style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"14px 16px" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                            <div>
                              <div style={{ fontSize:13, fontWeight:500, color:L.text }}>{fmt(req.amountEUR)}</div>
                              <div style={{ fontSize:10, color:L.muted, marginTop:2 }}>
                                {req.payerId ? `From @${req.payerName || req.payerId}` : "Open link"}
                                {req.note && <span style={{ marginLeft:6 }}>· {req.note}</span>}
                              </div>
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <StatusBadge status={req.status}/>
                              {req.status === "pending" && (
                                <button onClick={() => handleCancel(req.id)}
                                  style={{ background:"none", border:"none", cursor:"pointer", color:L.muted, padding:2 }}>
                                  <X size={13}/>
                                </button>
                              )}
                            </div>
                          </div>
                          {req.status === "pending" && (
                            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                              <button onClick={() => copyText(req.id, `${PAYMENT_BASE}/${req.id}`)}
                                style={{ display:"flex", alignItems:"center", gap:5, background:"transparent", border:`1px solid ${L.border}`, padding:"6px 12px", cursor:"pointer", fontSize:9, color:L.textSoft, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.12em" }}>
                                {copied===req.id?<><Check size={10}/> COPIED</>:<><Copy size={10}/> COPY LINK</>}
                              </button>
                              <span style={{ fontSize:9, color:L.muted }}>
                                Expires {req.expiresAt ? new Date(req.expiresAt).toLocaleDateString("en-GB", { day:"numeric", month:"short" }) : "—"}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* ════════════════════════════════════════
                TAB: MOBILE MONEY
            ════════════════════════════════════════ */}
            {activeTab === "mobile" && (
              <motion.div key="mobile" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}>
                <div style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"24px 22px", marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:22, paddingBottom:18, borderBottom:`1px solid ${L.border}` }}>
                    <div style={{ width:44, height:44, background:D.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <Smartphone size={20} color={D.gold} strokeWidth={1.5}/>
                    </div>
                    <div>
                      <p style={{ fontSize:13, fontWeight:600, color:L.text, letterSpacing:"0.06em" }}>Mobile Money</p>
                      <p style={{ fontSize:10, color:L.muted, marginTop:2 }}>Share your paybill number to receive</p>
                    </div>
                  </div>

                  {/* Provider selector */}
                  <p style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", marginBottom:12 }}>SELECT PROVIDER</p>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:22 }}>
                    {Object.entries(MOBILE_PAYBILLS).map(([key, cfg]) => (
                      <button key={key} onClick={() => setActiveMobileProvider(key)}
                        style={{
                          display:"flex", alignItems:"center", gap:6, padding:"8px 14px",
                          border: activeMobileProvider===key?`2px solid ${L.text}`:`1px solid ${L.border}`,
                          background: activeMobileProvider===key?L.text:"transparent",
                          color: activeMobileProvider===key?L.bg:L.text,
                          cursor:"pointer", fontSize:11, fontWeight:500, fontFamily:"'Montserrat',sans-serif",
                        }}>
                        <span style={{ fontSize:15 }}>{cfg.flag}</span>
                        {cfg.label}
                      </button>
                    ))}
                  </div>

                  {/* Paybill details */}
                  <div style={{ background:D.bg, border:`1px solid ${D.border2}`, padding:"20px 20px", marginBottom:16 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                      <div>
                        <p style={{ fontSize:9, color:D.muted, letterSpacing:"0.2em", marginBottom:6 }}>{activeMobileCfg.label.toUpperCase()}</p>
                        <p style={{ fontSize:22, fontWeight:300, color:D.white, fontFamily:"'Cormorant Garamond',serif" }}>
                          {activeMobileCfg.paybill}
                        </p>
                      </div>
                      <span style={{ fontSize:28 }}>{activeMobileCfg.flag}</span>
                    </div>
                    <div style={{ height:1, background:D.border, marginBottom:14 }}/>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div>
                        <p style={{ fontSize:8, color:D.muted, letterSpacing:"0.2em", marginBottom:4 }}>PAYBILL / SHORT CODE</p>
                        <p style={{ fontSize:14, color:D.offwhite, fontFamily:"'Courier New',monospace" }}>{activeMobileCfg.paybill}</p>
                      </div>
                      <div>
                        <p style={{ fontSize:8, color:D.muted, letterSpacing:"0.2em", marginBottom:4 }}>ACCOUNT NUMBER</p>
                        <p style={{ fontSize:14, color:D.gold, fontFamily:"'Courier New',monospace" }}>{userId.slice(0,12).toUpperCase()}</p>
                      </div>
                    </div>
                  </div>

                  <CopyField label="PAYBILL / SHORTCODE" value={activeMobileCfg.paybill} mono/>
                  <CopyField label="ACCOUNT NUMBER (YOUR USER ID)" value={userId} mono/>

                  <div style={{ padding:"12px 14px", background:"#fffbf0", border:"1px solid #f0e8c8", display:"flex", alignItems:"flex-start", gap:8, marginTop:8 }}>
                    <Smartphone size={13} color="#b8860b" style={{ flexShrink:0, marginTop:1 }}/>
                    <span style={{ fontSize:10, color:"#b8860b", lineHeight:1.7 }}>
                      Always use your <strong>User ID as the account number</strong> when making mobile money payments to Cosmoola. Payments without a reference may take up to 5 business days to credit.
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ════════════════════════════════════════
                TAB: SEPA
            ════════════════════════════════════════ */}
            {activeTab === "sepa" && (
              <motion.div key="sepa" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}>
                <div style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"24px 22px", marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:22, paddingBottom:18, borderBottom:`1px solid ${L.border}` }}>
                    <div style={{ width:44, height:44, background:D.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <Building2 size={20} color={D.gold} strokeWidth={1.5}/>
                    </div>
                    <div>
                      <p style={{ fontSize:13, fontWeight:600, color:L.text, letterSpacing:"0.06em" }}>SEPA Bank Transfer</p>
                      <p style={{ fontSize:10, color:L.muted, marginTop:2 }}>EU/EEA banks · EUR only · 1–2 business days · Free</p>
                    </div>
                  </div>

                  <p style={{ fontSize:11, color:L.textSoft, lineHeight:1.8, marginBottom:22 }}>
                    Send a SEPA credit transfer from any EU or EEA bank. Always include your <strong>User ID as the payment reference</strong> so funds are credited automatically.
                  </p>

                  <CopyField label="ACCOUNT HOLDER"  value={BANK.local.accountHolder}/>
                  <CopyField label="IBAN"             value={BANK.local.iban} mono/>
                  <CopyField label="BIC / SWIFT"      value={BANK.local.bic} mono/>
                  <CopyField label="BANK"             value={BANK.local.bankName}/>
                  <CopyField label="PAYMENT REFERENCE — REQUIRED" value={userId} mono/>

                  <div style={{ padding:"14px 16px", background:"#f0f9f0", border:"1px solid #d4ecd4", marginTop:8 }}>
                    <p style={{ fontSize:10, color:"#2d7a2d", fontWeight:600, marginBottom:4, letterSpacing:"0.05em" }}>IMPORTANT — PAYMENT REFERENCE</p>
                    <p style={{ fontSize:11, color:"#2d7a2d", lineHeight:1.7 }}>
                      Always include your <strong>User ID ({userId || "your-user-id"})</strong> in the payment reference. Transfers without it may be delayed by up to 5 business days.
                    </p>
                  </div>

                  {/* Timeline */}
                  <div style={{ marginTop:24 }}>
                    <p style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", marginBottom:14 }}>PROCESSING TIMELINE</p>
                    {[
                      { n:"01", label:"You initiate transfer",    sub:"Day 0 — Send from your bank" },
                      { n:"02", label:"Bank processing",          sub:"Day 0–1 — SEPA instruction sent" },
                      { n:"03", label:"Received by Cosmoola",     sub:"Day 1–2 — Transfer lands" },
                      { n:"04", label:"Wallet credited",          sub:"Day 1–2 — Balance updated automatically" },
                    ].map((s,i) => (
                      <div key={s.n} style={{ display:"flex", gap:14, paddingBottom:16, position:"relative" }}>
                        {i < 3 && <div style={{ position:"absolute", left:15, top:28, width:1, height:"calc(100% - 14px)", background:L.border }}/>}
                        <div style={{ width:30, height:30, borderRadius:"50%", background:D.bg, color:D.gold, fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{s.n}</div>
                        <div style={{ paddingTop:4 }}>
                          <p style={{ fontSize:12, fontWeight:500, color:L.text }}>{s.label}</p>
                          <p style={{ fontSize:10, color:L.muted, marginTop:2, lineHeight:1.6 }}>{s.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ════════════════════════════════════════
                TAB: SWIFT / INTERNATIONAL
            ════════════════════════════════════════ */}
            {activeTab === "swift" && (
              <motion.div key="swift" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}>
                <div style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"24px 22px", marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:22, paddingBottom:18, borderBottom:`1px solid ${L.border}` }}>
                    <div style={{ width:44, height:44, background:D.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <Globe size={20} color={D.gold} strokeWidth={1.5}/>
                    </div>
                    <div>
                      <p style={{ fontSize:13, fontWeight:600, color:L.text, letterSpacing:"0.06em" }}>International Wire (SWIFT)</p>
                      <p style={{ fontSize:10, color:L.muted, marginTop:2 }}>All currencies · 3–5 business days</p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:22 }}>
                    {[
                      { label:"MIN",     value:"€100"    },
                      { label:"ARRIVAL", value:"3–5 days"},
                      { label:"FX FEE",  value:"0.5%"    },
                    ].map(s => (
                      <div key={s.label} style={{ background:L.surface2, border:`1px solid ${L.border}`, padding:"14px 12px", textAlign:"center" }}>
                        <p style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", marginBottom:6 }}>{s.label}</p>
                        <p style={{ fontSize:16, fontWeight:300, color:L.text, fontFamily:"'Cormorant Garamond',serif" }}>{s.value}</p>
                      </div>
                    ))}
                  </div>

                  <p style={{ fontSize:10, color:L.muted, letterSpacing:"0.2em", marginBottom:14 }}>BENEFICIARY DETAILS</p>
                  <CopyField label="BENEFICIARY NAME"  value={BANK.international.accountHolder}/>
                  <CopyField label="IBAN"              value={BANK.international.iban} mono/>
                  <CopyField label="SWIFT / BIC"       value={BANK.international.bic} mono/>

                  <p style={{ fontSize:10, color:L.muted, letterSpacing:"0.2em", marginTop:20, marginBottom:14 }}>CORRESPONDENT BANK (USD / GBP)</p>
                  <CopyField label="CORRESPONDENT BANK" value={BANK.international.correspondentBank}/>
                  <CopyField label="CORRESPONDENT BIC"  value={BANK.international.correspondentBIC} mono/>
                  <CopyField label="PAYMENT REFERENCE — REQUIRED" value={`INTL-${userId || "YOUR-USER-ID"}`} mono/>

                  <div style={{ padding:"14px 16px", background:"#fffbf0", border:"1px solid #f0e8c8", marginTop:8 }}>
                    <p style={{ fontSize:10, color:"#b8860b", fontWeight:600, marginBottom:4 }}>REFERENCE — REQUIRED</p>
                    <p style={{ fontSize:11, color:"#b8860b", lineHeight:1.7 }}>
                      Include <strong>INTL-{userId || "YOUR-USER-ID"}</strong> in the payment details / reference field. Without this, crediting your wallet may take 5–10 additional business days.
                    </p>
                  </div>

                  {/* FAQ */}
                  <div style={{ marginTop:24 }}>
                    <p style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", marginBottom:14 }}>FREQUENTLY ASKED</p>
                    {[
                      { q:"Which currencies do you accept?", a:"We accept USD, GBP, CHF, JPY, AUD, CAD, SGD, AED and most major currencies. Your bank converts to EUR before settlement." },
                      { q:"Are there incoming wire fees?",  a:"Cosmoola charges 0.5% for FX conversion on international wires. Incoming EUR wires are free. Your own bank may charge an outgoing wire fee." },
                      { q:"My transfer hasn't arrived?",   a:"Contact support@cosmoola.com with your SWIFT MT103 confirmation. We'll trace the payment within 2 business days." },
                    ].map((item,i) => (
                      <div key={i} style={{ padding:"14px 0", borderTop:`1px solid ${L.border}` }}>
                        <p style={{ fontSize:11, fontWeight:600, color:L.text, marginBottom:6, letterSpacing:"0.03em" }}>{item.q}</p>
                        <p style={{ fontSize:11, color:L.textSoft, lineHeight:1.7 }}>{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {/* ── RIGHT SIDEBAR ── */}
        <div className="rt-sidebar">
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4, delay:0.2 }} style={{ position:"sticky", top:80 }}>

            {/* Profile card */}
            <div style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"24px 20px", marginBottom:16 }}>
              <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:16 }}>YOUR RECEIVE DETAILS</p>

              {myUsername && (
                <div style={{ marginBottom:14 }}>
                  <p style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", marginBottom:6 }}>COSMOOLA USERNAME</p>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:L.surface2, border:`1px solid ${L.border}`, padding:"11px 14px" }}>
                    <span style={{ fontSize:15, fontWeight:500, color:L.text }}>@{myUsername}</span>
                    <button onClick={() => copyText("sidebar_user", `@${myUsername}`)}
                      style={{ background:"none", border:"none", cursor:"pointer", color:copied==="sidebar_user"?"#2d7a2d":L.muted, fontSize:10, display:"flex", alignItems:"center", gap:4 }}>
                      {copied==="sidebar_user"?<><Check size={11}/> COPIED</>:<><Copy size={11}/> COPY</>}
                    </button>
                  </div>
                </div>
              )}

              <div style={{ marginBottom:14 }}>
                <p style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", marginBottom:6 }}>USER ID</p>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:L.surface2, border:`1px solid ${L.border}`, padding:"11px 14px" }}>
                  <span style={{ fontSize:11, color:L.text, fontFamily:"'Courier New',monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{userId}</span>
                  <button onClick={() => copyText("sidebar_uid", userId)}
                    style={{ background:"none", border:"none", cursor:"pointer", color:copied==="sidebar_uid"?"#2d7a2d":L.muted, fontSize:10, display:"flex", alignItems:"center", gap:4, flexShrink:0, marginLeft:8 }}>
                    {copied==="sidebar_uid"?<><Check size={11}/> COPIED</>:<><Copy size={11}/> COPY</>}
                  </button>
                </div>
              </div>

              <div style={{ height:1, background:L.border, margin:"16px 0" }}/>

              {/* Quick nav */}
              {[
                { label:"Add Money",    path:`/AddMoney/${userId}`   },
                { label:"Transfer Out", path:`/Transfer/${userId}`   },
                { label:"Transactions", path:`/transactions/${userId}` },
              ].map(item => (
                <button key={item.label} onClick={() => navigate(item.path)}
                  style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 0", background:"none", border:"none", borderBottom:`1px solid ${L.border}`, cursor:"pointer", color:L.text, fontSize:11, fontFamily:"inherit", letterSpacing:"0.05em" }}>
                  {item.label}<ChevronRight size={12} color={L.muted}/>
                </button>
              ))}
            </div>

            {/* Security box */}
            <div style={{ background:D.bg, border:`1px solid ${D.border}`, padding:"20px 18px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                <Shield size={14} color={D.gold}/>
                <span style={{ fontSize:10, color:D.gold, letterSpacing:"0.15em", fontWeight:600 }}>SECURITY</span>
              </div>
              {[
                "All transfers verified by Cosmoola",
                "Bank-level encryption",
                "Payment requests expire in 72 hours",
                "Cosmoola-to-Cosmoola — no fees",
                "Real-time fraud monitoring",
              ].map(item => (
                <div key={item} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <div style={{ width:4, height:4, borderRadius:"50%", background:D.gold, flexShrink:0 }}/>
                  <span style={{ fontSize:10, color:D.muted }}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ background:D.bg, borderTop:`1px solid ${D.border}`, padding:"28px 24px", marginTop:40 }}>
        <div style={{ maxWidth:1200, margin:"0 auto", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
          <div style={{ fontSize:11, color:D.muted }}>© 2025–{new Date().getFullYear()} Cosmoola Technologies</div>
          <div style={{ display:"flex", gap:20 }}>
            {["privacy","terms","support"].map(p => (
              <span key={p} onClick={() => navigate(`/${p}`)} style={{ fontSize:11, color:D.muted, cursor:"pointer", textDecoration:"underline", textUnderlineOffset:3 }}>
                {p.charAt(0).toUpperCase()+p.slice(1)}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ReceiveTransfer;

/* ─────────────────────────────────────────────────────────
   CSS
───────────────────────────────────────────────────────── */
const RT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#fff}button{font-family:inherit}input{font-family:inherit}
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}
  input[type=number]{-moz-appearance:textfield}
  ::-webkit-scrollbar{width:0;height:0}

  @keyframes rt-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .rt-spin{animation:rt-spin 1s linear infinite}

  .rt-container{
    max-width:1200px; margin:0 auto;
    padding:32px 24px 0;
    display:grid;
    grid-template-columns:1fr 320px;
    gap:28px;
    align-items:start;
  }
  .rt-main{min-width:0}
  .rt-sidebar{min-width:0}
  .rt-back-label{display:inline}
  .rt-tab-label{display:inline}

  @media(max-width:900px){
    .rt-container{
      grid-template-columns:1fr !important;
      gap:0 !important;
      padding:20px 16px 0 !important;
    }
    .rt-sidebar{display:none}
    .rt-back-label{display:none}
    .rt-tab-label{display:none}
  }
`;